/**
 * @deprecated StatusTooltip1 is deprecated. It still uses ResultItem2 intentionally —
 * do not upgrade to ResultItem3 here. New code should avoid using this component.
 */
import { useRef, useEffect, useCallback, useState } from 'react';
import {
  useFloating,
  arrow,
  flip,
  shift,
  FloatingArrow,
  autoUpdate,
  useClick,
  useDismiss,
  useInteractions,
} from '@floating-ui/react';
import { XMarkIcon } from '@heroicons/react/16/solid';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { updateObjective } from '../../store/map3PendingSlice';
import { Objective1, Status1 } from '../../types';
import { upsertItem } from '../../utils/upsertItem';
import { useResultsAndComments } from '../../hooks/useIntervalData';
import { CommentBubble1 } from './CommentBubble1';
import { AddEditComment1 } from './AddEditComment1';
import { ResultItem2 } from '../dev/ResultItem2';
import { AddResultButton1 } from '../dev/AddResultButton1';
import { getShortIdFromUuid } from '@mm-shared/utils/getShortIdFromUuid';
import { isMetricObjective } from '../../utils/kpiUtils';
import { calculateContinuousCode } from '../../utils/kpiUtils';
import { getBackgroundColorClass } from '../../utils/colorUtils';

interface StatusTooltip1Props {
  cellId: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isInUse: boolean;
  setIsInUse: (isInUse: boolean) => void;
  onClose: () => void;
  referenceElement: HTMLElement | null;
  statusUuid: string;
  objectiveUuid: string;
  forIntervalEnding: string;
  milestoneMapUuid: string;
  canEdit?: boolean;
}

export function StatusTooltip1({
  cellId,
  isOpen,
  setIsOpen,
  isInUse,
  setIsInUse,
  onClose,
  referenceElement,
  statusUuid,
  objectiveUuid,
  forIntervalEnding,
  milestoneMapUuid,
  canEdit = true,
}: StatusTooltip1Props) {
  const arrowRef = useRef(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj: Objective1) => obj.uuid === objectiveUuid),
  );
  const status = objective?.statuses.find((s: Status1) => s.uuid === statusUuid);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsInUse(false);
    // Find the parent td element which is the actual focusable cell
    const cellElement = referenceElement?.closest('td');
    if (cellElement instanceof HTMLElement) {
      cellElement.focus();
    }
    onClose();
  }, [onClose, setIsOpen, setIsInUse, referenceElement]);

  const { refs, floatingStyles, context } = useFloating({
    whileElementsMounted: autoUpdate,
    placement: 'right',
    open: isOpen,
    onOpenChange: (isOpen: boolean) => {
      if (!isOpen) {
        handleClose();
      }
    },
    elements: {
      reference: referenceElement,
    },
    middleware: [
      flip(),
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      tooltipRef.current = node;
      refs.setFloating(node);
    },
    [refs],
  );

  useEffect(() => {
    if (isOpen && tooltipRef.current) {
      tooltipRef.current.focus();
    }
  }, [isOpen]);

  // Focus trapping for the tooltip
  const handleTooltipKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Handle Escape key to close tooltip - this runs before child components can stop propagation
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        handleClose();
        return;
      }

      if (e.key === 'Tab') {
        const focusableElements = tooltipRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );

        if (focusableElements && focusableElements.length > 0) {
          const firstElement = focusableElements[0] as HTMLElement;
          const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

          if (e.shiftKey) {
            // Shift+Tab: if on first element, wrap to last
            if (document.activeElement === firstElement) {
              e.preventDefault();
              lastElement.focus();
            }
          } else {
            // Tab: if on last element, wrap to first
            if (document.activeElement === lastElement) {
              e.preventDefault();
              firstElement.focus();
            }
          }
        }
      }
    },
    [handleClose],
  );

  const dismiss = useDismiss(context, {
    escapeKey: true,
    outsidePress: true,
    referencePress: false,
    bubbles: false,
  });
  const click = useClick(context);
  const { getFloatingProps } = useInteractions([click, dismiss]);

  if (!isOpen) return null;

  return (
    <div
      id={`floating-${cellId}`}
      ref={setRefs}
      style={{
        ...floatingStyles,
        position: 'fixed',
      }}
      /**
       * We need to explicitly tell floating UI that ESC is the only key we
       * want it to manage. (This is a common gotcha with floating UI libraries -
       * they often have their own event handling that can interfere with
       * normal keyboard navigation. By explicitly configuring which keyboard
       * events should be handled at the floating container level, we've
       * restored the expected keyboard behavior for the button while
       * maintaining the escape-to-close functionality.
       */
      {...getFloatingProps({
        onKeyDown: (e) => {
          handleTooltipKeyDown(e);
          if (e.key !== 'Escape') {
            e.stopPropagation();
          }
        },
      })}
      className="bg-slate-950 p-1 rounded-xl shadow-lg z-[9999] max-h-[90vh] max-w-[90vw] flex flex-col"
      onFocus={() => setIsInUse(true)}
      tabIndex={-1}
    >
      <div className="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white text-zinc:500 dark:border-white/10 dark:bg-zinc-800 dark:text-zinc-300 drop-shadow-2xl z-50 max-w-md w-full flex flex-col max-h-full">
        <TooltipHeader
          onClose={handleClose}
          forIntervalEnding={forIntervalEnding}
          statusUuid={status?.uuid || ''}
          objectiveUuid={objective?.uuid || ''}
        />
        <div className="overflow-y-auto flex-1 min-h-0">
          <TooltipStatus
            forIntervalEnding={forIntervalEnding}
            statusUuid={status?.uuid || ''}
            objectiveUuid={objective?.uuid || ''}
            onClose={handleClose}
            milestoneMapUuid={milestoneMapUuid}
            canEdit={canEdit}
          />
          <hr className="border-t border-gray-200 dark:border-gray-700" />
          <TooltipResults
            forIntervalEnding={forIntervalEnding}
            statusUuid={status?.uuid || ''}
            objectiveUuid={objective?.uuid || ''}
            milestoneMapUuid={milestoneMapUuid}
          />
          <hr className="border-t border-gray-200 dark:border-gray-700 pb-6" />
          <TooltipComments
            forIntervalEnding={forIntervalEnding}
            objectiveUuid={objective?.uuid || ''}
            milestoneMapUuid={milestoneMapUuid}
          />
        </div>
      </div>
      <FloatingArrow ref={arrowRef} context={context} />
    </div>
  );
}

function TooltipHeader({
  onClose,
  forIntervalEnding,
  statusUuid,
  objectiveUuid,
}: {
  onClose: () => void;
  forIntervalEnding: string;
  statusUuid: string;
  objectiveUuid: string;
}) {
  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj: Objective1) => obj.uuid === objectiveUuid),
  );

  const isLoading = useSelector((state: RootState) => state.map3Data.isLoading);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!objective?.uuid) {
    console.error(`Objective not found: ${objectiveUuid}`);
    return <div>Error loading objective</div>;
  }

  const date = new Date(forIntervalEnding);
  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  // TODO: make this dynamic based on map settings (interval might not be "week" ending).
  const text = `Status as of ${formattedDate}`;

  return (
    <div className="px-4 pt-4 sm:px-6">
      <XMarkIcon
        className="h-6 w-6 text-black dark:text-white flex ml-auto hover:bg-blue-600 hover:text-white"
        onClick={onClose} // This will now use the same handleClose function
      />
      <div className={`w-full h-5`}>
        <div className="text-center text-sm font-bold">{text}</div>
      </div>
    </div>
  );
}

/**
 * @param forIntervalEnding - ISO string
 * @returns
 */
function TooltipStatus({
  forIntervalEnding,
  statusUuid,
  objectiveUuid,
  milestoneMapUuid,
  onClose,
  canEdit = true,
}: {
  forIntervalEnding: string;
  statusUuid: string;
  objectiveUuid: string;
  milestoneMapUuid: string;
  onClose: () => void;
  canEdit?: boolean;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const { results } = useResultsAndComments(forIntervalEnding, objectiveUuid, milestoneMapUuid);
  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj: Objective1) => obj.uuid === objectiveUuid),
  );
  const isLoading = useSelector((state: RootState) => state.map3Data.isLoading);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!objective?.uuid) {
    console.error(`Objective not found: ${objectiveUuid}`);
    return <div>Error loading objective</div>;
  }

  const status = objective?.statuses.find((status: Status1) => status.uuid === statusUuid);
  const hasResults = results.length > 0;
  const hasKeyResults = results.some((result) => result.isKeyResult);

  const handleStatusUpdate = (statusCode: '?' | 'p' | 'g' | 'y' | 'r') => {
    if (!canEdit) {
      console.warn('Status update denied: user does not have edit permissions');
      return;
    }

    const now = new Date().toISOString();
    const uuid = crypto.randomUUID();

    const newStatus = {
      uuid: status?.uuid || uuid,
      shortId: status?.shortId || getShortIdFromUuid(uuid),
      forIntervalEnding,
      createdDate: status?.createdDate || now,
      lastUpdatedDate: now,
      statusCode,
    };

    // Get the existing statuses array
    const existingStatuses = objective?.statuses || [];

    // Create updated statuses array with the new status
    const updatedStatuses = upsertItem(
      existingStatuses,
      newStatus,
      (item) => item.uuid === statusUuid,
    );

    // Dispatch the update with the entire updated statuses array
    dispatch(
      updateObjective({
        uuid: objective.uuid,
        updates: {
          statuses: updatedStatuses,
        },
      }),
    );
  };

  // Define the description sets
  const defaultDescriptions = {
    '?': 'To be determined, work may happen here',
    p: 'Work is planned/expected here',
    g: 'On track to hit next inchstone or milestone',
    y: 'Unable to hit next inchstone, but able to hit the next milestone',
    r: 'Next milestone cannot be hit without adjusting timeline, scope, or resources',
  };

  const resultsOnlyDescriptions = {
    '?': 'TBD (This status is not applicable here)',
    p: 'Work is planned/expected here',
    g: 'Inchstone hit (checked results affirm work is on track to hit next milestone)',
    y: 'Inchstone not hit (targeted results unchecked, but the next milestone is still achievable)',
    r: 'Next milestone cannot be hit without adjusting timeline, scope, or resources',
  };

  const keyResultsDescriptions = {
    '?': 'TBD (This status is not applicable here)',
    p: 'Work is planned/expected here',
    g: 'Milestone hit, key results delivered!',
    y: 'Milestone partially hit, key results partially delivered',
    r: 'Milestone not hit, key results not delivered',
  };

  // Select the appropriate description set
  const descriptions = hasKeyResults
    ? keyResultsDescriptions
    : hasResults && !hasKeyResults
      ? resultsOnlyDescriptions
      : defaultDescriptions;

  const currentStatusCode = status?.statusCode || '';

  // Check if objective is metric-oriented
  const isMetric = isMetricObjective(objective.type);

  // Metric input mode
  if (isMetric && objective.kpiSettings) {
    return (
      <MetricInput
        objective={objective}
        status={status}
        forIntervalEnding={forIntervalEnding}
        canEdit={canEdit}
        onClose={onClose}
      />
    );
  }

  // Discrete input mode (deliverable-oriented)
  return (
    <div className="text-sm">
      <div className="px-4 py-4 sm:px-6">
        <div className="flex flex-col space-y-2">
          <StatusButton
            statusCode="?"
            bgClass="bg-status-tbd"
            onClick={() => handleStatusUpdate('?')}
            description={descriptions['?']}
            isSelected={currentStatusCode === '?'}
          />
          <StatusButton
            statusCode="p"
            bgClass="bg-status-p"
            onClick={() => handleStatusUpdate('p')}
            description={descriptions['p']}
            isSelected={currentStatusCode === 'p'}
          />
          <StatusButton
            statusCode="g"
            bgClass="bg-status-g"
            onClick={() => handleStatusUpdate('g')}
            description={descriptions['g']}
            isSelected={currentStatusCode === 'g'}
          />
          <StatusButton
            statusCode="y"
            bgClass="bg-status-y"
            onClick={() => handleStatusUpdate('y')}
            description={descriptions['y']}
            isSelected={currentStatusCode === 'y'}
          />
          <StatusButton
            statusCode="r"
            bgClass="bg-status-r"
            onClick={() => handleStatusUpdate('r')}
            description={descriptions['r']}
            isSelected={currentStatusCode === 'r'}
          />
        </div>
      </div>
    </div>
  );
}

function MetricInput({
  objective,
  status,
  forIntervalEnding,
  canEdit,
  onClose,
}: {
  objective: Objective1;
  status: Status1 | undefined;
  forIntervalEnding: string;
  canEdit: boolean;
  onClose: () => void;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const [inputValue, setInputValue] = useState<number>(status?.value || 0);

  const kpiSettings = objective.kpiSettings!;
  const { ranges, higherIsBetter, numberType } = kpiSettings;

  // Calculate min/max for slider based on ranges
  const min = higherIsBetter
    ? ranges.worstRed
    : ranges.bestGreen;
  const max = higherIsBetter
    ? ranges.bestGreen
    : ranges.worstRed;

  // Calculate continuous code from current value
  const continuousCode = calculateContinuousCode(inputValue, kpiSettings);
  const bgColorClass = getBackgroundColorClass(continuousCode);

  const handleValueChange = (newValue: number) => {
    setInputValue(newValue);
  };

  const handleSave = () => {
    if (!canEdit) {
      console.warn('Status update denied: user does not have edit permissions');
      return;
    }

    const now = new Date().toISOString();
    const uuid = crypto.randomUUID();

    const newStatus: Status1 = {
      uuid: status?.uuid || uuid,
      shortId: status?.shortId || getShortIdFromUuid(uuid),
      forIntervalEnding,
      createdDate: status?.createdDate || now,
      lastUpdatedDate: now,
      statusCode: continuousCode,
      value: inputValue,
    };

    const updatedStatuses = upsertItem(
      objective?.statuses || [],
      newStatus,
      (item) => item.uuid === (status?.uuid || uuid),
    );

    dispatch(
      updateObjective({
        uuid: objective.uuid,
        updates: {
          statuses: updatedStatuses,
        },
      }),
    );
  };

  return (
    <div className="text-sm px-4 py-4 sm:px-6 space-y-4">
      {/* Current Value Display */}
      <div className="text-center">
        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {inputValue}
          {numberType === '%' ? '%' : ''}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Status Code: <span className="font-mono">{continuousCode}</span>
        </div>
      </div>

      {/* Color Preview */}
      <div className={`h-12 rounded-md ${bgColorClass} flex items-center justify-center`}>
        <span className="text-sm font-medium">Current Performance Level</span>
      </div>

      {/* Range Slider */}
      <div className="space-y-2">
        <input
          type="range"
          min={min}
          max={max}
          step={numberType === '%' ? 1 : 0.1}
          value={inputValue}
          onChange={(e) => handleValueChange(Number(e.target.value))}
          disabled={!canEdit}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 disabled:opacity-50"
        />
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>
            {min}
            {numberType === '%' ? '%' : ''}
          </span>
          <span>
            {max}
            {numberType === '%' ? '%' : ''}
          </span>
        </div>
      </div>

      {/* Visual Color Guide */}
      <div className="space-y-1">
        <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
          Performance Zones:
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="space-y-1">
            <div className="bg-status-r1 h-6 rounded flex items-center justify-center">
              <span className="text-white font-medium">Red</span>
            </div>
            <div className="text-center text-gray-600 dark:text-gray-400">
              {higherIsBetter ? `< ${ranges.worstYellow}` : `> ${ranges.worstYellow}`}
              {numberType === '%' ? '%' : ''}
            </div>
          </div>
          <div className="space-y-1">
            <div className="bg-status-y7 h-6 rounded flex items-center justify-center">
              <span className="font-medium">Yellow</span>
            </div>
            <div className="text-center text-gray-600 dark:text-gray-400">
              {higherIsBetter
                ? `${ranges.worstYellow}-${ranges.bestYellow}`
                : `${ranges.bestYellow}-${ranges.worstYellow}`}
              {numberType === '%' ? '%' : ''}
            </div>
          </div>
          <div className="space-y-1">
            <div className="bg-status-g22 h-6 rounded flex items-center justify-center">
              <span className="text-white font-medium">Green</span>
            </div>
            <div className="text-center text-gray-600 dark:text-gray-400">
              {higherIsBetter ? `> ${ranges.bestYellow}` : `< ${ranges.bestYellow}`}
              {numberType === '%' ? '%' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      {canEdit && (
        <div className="pt-2">
          <button
            onClick={handleSave}
            className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Save Metric Value
          </button>
        </div>
      )}
    </div>
  );
}

function StatusButton({
  statusCode,
  bgClass,
  onClick,
  description,
  isSelected,
}: {
  statusCode: string;
  bgClass: string;
  onClick: () => void;
  description: string;
  isSelected: boolean;
}) {
  const showStatusCodes = useSelector(
    (state: RootState) => state.map3UI.mapControls.showStatusCodes,
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault(); // Prevent page scroll on space
      onClick();
    }
  };

  return (
    <div
      className={`flex items-center space-x-3 p-1 rounded-md cursor-pointer group transition-all duration-150
        ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-500' : 'hover:bg-gray-50 dark:hover:bg-gray-700/20'}`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`Set status to ${statusCode}: ${description}`}
      aria-pressed={isSelected}
    >
      <div
        className={`w-8 h-8 ${bgClass} rounded-md flex items-center justify-center flex-shrink-0 
          ${
            isSelected
              ? 'ring-1 ring-blue-500 shadow-lg'
              : 'group-hover:outline group-hover:outline-2 group-hover:outline-blue-500 group-focus:outline group-focus:outline-2 group-focus:outline-blue-500'
          }`}
      >
        <span className="text-center">{showStatusCodes ? statusCode : ''}</span>
      </div>
      <span className={`text-sm flex-1 ${isSelected ? 'font-medium' : ''}`}>{description}</span>
    </div>
  );
}

/**
 * View, add, update, delete results.
 *
 * Note about how results are found and loaded:
 * - Get interval settings from milestoneMap
 * - Create a date range for valid result deadlines. (It's not safe to assume results have a deadline that perfectly matches the column of the table we're in right now. If this objective is included in a different milestone map, or if the user has changed settings, or if we support manually set result dates, we need to be able to handle these cases.)
 * - Get any results that have a deadline that falls within the interval ending at the status's forIntervalEnding date.
 */
function TooltipResults({
  forIntervalEnding,
  statusUuid,
  objectiveUuid,
  milestoneMapUuid,
}: {
  forIntervalEnding: string;
  statusUuid: string;
  objectiveUuid: string;
  milestoneMapUuid: string;
}) {
  const { results } = useResultsAndComments(forIntervalEnding, objectiveUuid, milestoneMapUuid);

  return (
    <div className="px-4 py-4 sm:px-6">
      {results.map((result) => (
        <div
          key={result.uuid}
          className="flex items-start space-x-2 relative rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 p-1"
        >
          <ResultItem2
            resultUuid={result.uuid}
            objectiveUuid={objectiveUuid}
            deadline={result.deadline!}
          />
        </div>
      ))}
      <AddResultButton1 objectiveUuid={objectiveUuid} latestIntervalEnding={forIntervalEnding} />
    </div>
  );
}

function TooltipComments({
  forIntervalEnding,
  objectiveUuid,
  milestoneMapUuid,
}: {
  forIntervalEnding: string;
  objectiveUuid: string;
  milestoneMapUuid: string;
}) {
  const { comments } = useResultsAndComments(forIntervalEnding, objectiveUuid, milestoneMapUuid);

  return (
    <div className="px-4 py-4 sm:px-6">
      {comments.length > 0 && (
        <ul className="-mt-10 mb-6" role="list" aria-label="Comments">
          {comments.map((comment, commentIdx) => (
            <li key={comment.uuid}>
              <CommentBubble1
                commentUuid={comment.uuid}
                showTopBorder={commentIdx !== 0}
                parentObjectiveUuid={objectiveUuid}
              />
            </li>
          ))}
        </ul>
      )}

      <AddEditComment1 objectiveUuid={objectiveUuid} forIntervalEnding={forIntervalEnding} />
    </div>
  );
}
